import React from 'react';

import { useAppDispatch } from '../../../../store/store';
import { fetchExaminations } from '../../../../store/slices/examinations';
import { client } from '../../../../networking/client';
import { toast } from 'react-toastify';
import { SalesOrderInfo } from '../../../../store/slices/salesOrders';
import { CancelDialog } from './CancelDialog';
import classes from './ExaminationsTab.module.css';
import { useTranslation } from '../../../../hooks/useTranslations';

type Props = {
  selectedSalesOrder: SalesOrderInfo;
  hasStartedExamination: boolean;
  isExaminationComplete: boolean;
  handleStartExamination: () => void;
  loading: boolean;
  setLoading: (value: boolean) => void;
  startedNewExamination: boolean;
  setStartedNewExamination: (value: boolean) => void;
};

const ExaminationButtons: React.FC<Props> = ({
  selectedSalesOrder,
  hasStartedExamination,
  isExaminationComplete,
  handleStartExamination,
  loading,
  setLoading,
  startedNewExamination,
  setStartedNewExamination,
}) => {
  const { translations } = useTranslation();
  const dispatch = useAppDispatch();
  const [showCancelDialog, setShowCancelDialog] = React.useState<boolean>(false);
  const [showFinalizeDialog, setShowFinalizeDialog] = React.useState<boolean>(false);

  const cancelExamination = () => {
    setLoading(true);
    client
      .delete(`/healthpoint/cancel-examination/${selectedSalesOrder.work_order?.work_order_id}`)
      .then(() => {
        setShowCancelDialog(false);
        if (selectedSalesOrder.work_order?.work_order_id) {
          dispatch(fetchExaminations(selectedSalesOrder.work_order.work_order_id));
        }
        setShowCancelDialog(false);
        setStartedNewExamination(false);
      })
      .catch((error) => {
        toast.error(error.response?.data?.message ?? error.message, {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
      })
      .finally(() => setLoading(false));
  };

  const deletePatient = () => {
    setLoading(true);
    client
      .delete(`/healthpoint/delete-patient/${selectedSalesOrder.work_order?.work_order_id}`)
      .then(() => {
        setShowFinalizeDialog(false);
        if (selectedSalesOrder.work_order?.work_order_id) {
          dispatch(fetchExaminations(selectedSalesOrder.work_order.work_order_id));
        }
      })
      .catch((error) => {
        toast.error(error.response?.data?.error ?? error.message, {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
      })
      .finally(() => setLoading(false));
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
      {!isExaminationComplete ? (
        <button
          onClick={() => handleStartExamination()}
          disabled={
            loading || !(!hasStartedExamination || isExaminationComplete) || startedNewExamination
          }
          className={classes['btn-transparent']}
        >
          {translations('examinations.start')}
        </button>
      ) : (
        <button
          onClick={() => setShowCancelDialog(true)}
          disabled={loading || startedNewExamination}
          className={classes['btn-transparent']}
        >
          {translations('examinations.retake')}
        </button>
      )}
      <button
        onClick={() => setShowCancelDialog(true)}
        disabled={
          loading || !(!isExaminationComplete && hasStartedExamination) || !startedNewExamination
        }
        className={classes['btn-transparent']}
      >
        {translations('examinations.cancel')}
      </button>
      {startedNewExamination && (
        <button
          onClick={() => setStartedNewExamination(false)}
          disabled={loading}
          className={classes['btn-transparent']}
        >
          {translations('examinations.fetch')}
        </button>
      )}
      {showCancelDialog && (
        <CancelDialog
          isOpen={showCancelDialog}
          onClose={() => setShowCancelDialog(false)}
          handleCancel={() => {
            if (!isExaminationComplete && hasStartedExamination) {
              cancelExamination();
            } else {
              handleStartExamination();
              setShowCancelDialog(false);
            }
          }}
          isLoading={loading}
          header={'examinations.dialog.header'}
          message={'examinations.dialog.text'}
          subMessage={'examinations.dialog.subtext'}
        />
      )}
      {showFinalizeDialog && (
        <CancelDialog
          isOpen={showFinalizeDialog}
          onClose={() => setShowFinalizeDialog(false)}
          handleCancel={() => deletePatient()}
          isLoading={loading}
          header={'examinations.dialog.finalize.header'}
          message={'examinations.dialog.finalize.text'}
        />
      )}
    </div>
  );
};

export { ExaminationButtons };
